import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import { authClient } from "@/auth/client";
import { authErrorMessage, PASSWORD_MAX_LENGTH } from "@/lib/auth-error";
import { CircleNotch, SignOut, Key, Shield, Eye, EyeSlash } from "@phosphor-icons/react";
import {
  WarmCard,
  FieldWrap,
  WarmInput,
} from "@/components/client/AccountForm";

function scorePassword(pw: string) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}

export function SecurityForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const strength = useMemo(() => scorePassword(newPassword), [newPassword]);
  const strengthLabels = [
    t("profile.security.strength.veryWeak"),
    t("profile.security.strength.weak"),
    t("profile.security.strength.fair"),
    t("profile.security.strength.good"),
    t("profile.security.strength.strong"),
  ];
  const strengthColors = [
    "oklch(70% 0.16 25)",
    "oklch(72% 0.16 50)",
    "oklch(78% 0.16 80)",
    "oklch(70% 0.14 130)",
    "oklch(62% 0.15 150)",
  ];

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t("profile.security.passwordMismatch"));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t("profile.security.passwordTooShort"));
      return;
    }
    if (newPassword.length > PASSWORD_MAX_LENGTH) {
      toast.error(t("auth.errors.passwordTooLong"));
      return;
    }
    setIsChanging(true);
    try {
      const { error } = await authClient.changePassword({
        currentPassword,
        newPassword,
      });
      if (error) {
        toast.error(authErrorMessage(error, t, "profile.security.changeError"));
        return;
      }
      toast.success(t("profile.security.changeSuccess"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch {
      toast.error(t("profile.security.changeError"));
    } finally {
      setIsChanging(false);
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await authClient.signOut();
      navigate({ to: "/" });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("profile.security.signOutError")
      );
      setIsSigningOut(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Password card */}
      <WarmCard>
        <form onSubmit={handleChangePassword}>
          <div className="space-y-6 p-6 sm:p-8">
            <header className="flex items-start gap-4">
              <div
                className="flex size-11 shrink-0 items-center justify-center rounded-2xl"
                style={{
                  background: "var(--color-brand-bg-light)",
                  border: "1px solid var(--color-brand-bg)",
                }}
              >
                <Key
                  className="size-5"
                  style={{ color: "var(--color-brand-dark)" }}
                />
              </div>
              <div>
                <h3
                  className="font-heading text-xl font-semibold tracking-tight text-foreground"
                >
                  {t("profile.security.changePassword")}
                </h3>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {t("profile.security.changePasswordHint")}
                </p>
              </div>
            </header>

            <div className="space-y-5">
              <FieldWrap
                label={t("profile.security.currentPassword")}
                htmlFor="currentPassword"
              >
                <PasswordInput
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  show={showCurrent}
                  onToggle={() => setShowCurrent((v) => !v)}
                  autoComplete="current-password"
                  maxLength={PASSWORD_MAX_LENGTH}
                  required
                />
              </FieldWrap>

              <div className="grid gap-5 sm:grid-cols-2">
                <FieldWrap
                  label={t("profile.security.newPassword")}
                  htmlFor="newPassword"
                >
                  <PasswordInput
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    show={showNew}
                    onToggle={() => setShowNew((v) => !v)}
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={PASSWORD_MAX_LENGTH}
                    required
                  />
                  {newPassword && (
                    <div className="mt-2.5 space-y-1">
                      <div className="flex gap-1">
                        {[0, 1, 2, 3].map((i) => (
                          <span
                            key={i}
                            className="h-1 flex-1 rounded-full transition-all duration-300"
                            style={{
                              background:
                                strength > i
                                  ? strengthColors[strength]
                                  : "var(--color-warm-border)",
                            }}
                          />
                        ))}
                      </div>
                      <p
                        className="text-[10.5px] font-medium uppercase tracking-[0.1em]"
                        style={{ color: strengthColors[strength] }}
                      >
                        {strengthLabels[strength]}
                      </p>
                    </div>
                  )}
                </FieldWrap>

                <FieldWrap
                  label={t("profile.security.confirmPassword")}
                  htmlFor="confirmPassword"
                >
                  <PasswordInput
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    show={showNew}
                    onToggle={() => setShowNew((v) => !v)}
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={PASSWORD_MAX_LENGTH}
                    required
                    mismatch={
                      confirmPassword.length > 0 &&
                      confirmPassword !== newPassword
                    }
                  />
                </FieldWrap>
              </div>
            </div>
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4 sm:px-8"
            style={{
              borderColor: "var(--color-warm-border)",
              background: "oklch(98% 0.005 84 / 0.6)",
            }}
          >
            <Button
              type="submit"
              disabled={isChanging}
              className="rounded-full px-6"
            >
              {isChanging ? (
                <CircleNotch className="size-4 animate-spin" />
              ) : (
                <Shield className="size-4" />
              )}
              {t("profile.security.change")}
            </Button>
          </div>
        </form>
      </WarmCard>

      {/* Sign out card */}
      <div
        className="overflow-hidden rounded-3xl"
        style={{
          background:
            "linear-gradient(135deg, oklch(99% 0.003 30), oklch(97% 0.01 30))",
          border: "1px solid oklch(92% 0.02 30)",
        }}
      >
        <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="flex items-start gap-4">
            <div
              className="flex size-11 shrink-0 items-center justify-center rounded-2xl"
              style={{
                background: "oklch(96% 0.025 25)",
                border: "1px solid oklch(90% 0.04 25)",
              }}
            >
              <SignOut className="size-5" style={{ color: "oklch(55% 0.15 25)" }} />
            </div>
            <div>
              <h3
                className="font-heading text-lg font-semibold tracking-tight text-foreground"
              >
                {t("profile.security.signOutTitle")}
              </h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {t("profile.security.signOutHint")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={isSigningOut}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:scale-[1.02] disabled:opacity-60"
            style={{
              background: "oklch(100% 0 0)",
              color: "oklch(55% 0.15 25)",
              border: "1px solid oklch(90% 0.04 25)",
            }}
          >
            {isSigningOut ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              <SignOut className="size-4" />
            )}
            {t("profile.security.signOut")}
          </button>
        </div>
      </div>
    </div>
  );
}

interface PasswordInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  show: boolean;
  onToggle: () => void;
  mismatch?: boolean;
}

function PasswordInput({ show, onToggle, mismatch, ...rest }: PasswordInputProps) {
  return (
    <div className="relative">
      <WarmInput
        {...rest}
        type={show ? "text" : "password"}
        className={"pr-10 " + (rest.className ?? "")}
        style={
          mismatch
            ? ({
                borderColor: "oklch(75% 0.16 25)",
              } as React.CSSProperties)
            : undefined
        }
      />
      <button
        type="button"
        onClick={onToggle}
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-[oklch(98%_0.005_84)] hover:text-foreground"
      >
        {show ? <EyeSlash className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}
